/* Copyright (c) 2017 Backpack Health LLC */

/* global window */
import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import IconClose from './icons/IconClose';

class InputWithLabel extends React.Component {
  constructor(props) {
    super(props);

    this.inputRef = React.createRef();
    this.handleOnFocus = this.handleOnFocus.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.clearField = this.clearField.bind(this);

    this.removeCancelDelay = null;

    this.state = {
      hasFocus: !!this.props.alwayFocus,
      attempts: 0,
    };
  }

  componentDidMount() {
    if (this.props.initialFocus) {
      this.inputRef.current.focus();

      if (this.props.selectAllonFocus) {
        this.inputRef.current.select();
      }
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.removeCancelDelay);
    this.removeCancelDelay = null;
  }

  handleOnFocus(event) {
    if (this.removeCancelDelay) {
      window.clearTimeout(this.removeCancelDelay);
      this.removeCancelDelay = null;
    }

    if (this.props.selectAllonFocus) {
      if (this.inputRef.current) {
        this.inputRef.current.select();
      }
    }

    this.setState({
      hasFocus: true,
    });

    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  }


  handleOnBlur(event) {
    const attempts = !_.isEmpty(this.props.value) ? this.state.attempts + 1 : this.state.attempts;
    this.removeCancelDelay = window.setTimeout(() => {
      window.clearTimeout(this.removeCancelDelay);
      this.removeCancelDelay = null;
      this.setState({
        hasFocus: !!this.props.alwayFocus,
        attempts,
      });
    }, 250);

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  }

  handleOnChange(event) {
    if (this.props.onChange) {
      this.props.onChange(event);
    }
  }

  shouldDisplayInvalid() {
    return this.state.attempts >= this.props.attemptsBeforeInvalidHint;
  }

  shouldDisplayInvalidMessage() {
    return this.shouldDisplayInvalid() && !_.isEmpty(this.props.invalidHint);
  }

  clearField(event) {
    event.preventDefault();
    if (this.props.clearValue) {
      this.props.clearValue();
    }
    this.inputRef.current.focus();
  }

  renderRegularHint() {
    return (
      this.props.regularHint ? (
        <div>
          {this.props.regularHint}
        </div>
      ) : null
    );
  }

  renderCancelButton() {
    return (
      (this.props.isClearable && this.props.value && this.state.hasFocus) ? (
        <div className="input-icon--action" onClick={this.clearField}>
          <IconClose.Small color="gsMiddle" />
        </div>
      ) : null
    );
  }

  render() {
    const styleClass = classnames(
      'cmp-input-with-label',
      {
        'hide-label': this.props.hideLabel,
        'has-floating-label': !this.props.hasFixedLabel,
        'has-data': this.state.hasFocus || !_.isEmpty(this.props.value) || _.isNumber(this.props.value),
        'is-invalid': this.shouldDisplayInvalid() && this.props.isInvalid,
        'is-light': this.props.colorTheme === 'light',
        'has-forced-height': !this.props.noForcedHeight,
        'has-forced-label': !this.props.noForcedLabel,
        'always-floating': this.props.alwaysFloating,
        'is-clearable': this.props.isClearable,
      }
    );

    return (
      <div className={styleClass}>
        <input
          ref={this.inputRef}
          type={this.props.type}
          className=""
          name={this.props.name}
          autoComplete={this.props.autoComplete}
          autoCapitalize={this.props.autoCapitalize}
          value={this.props.value}
          autoFocus={this.props.autoFocus}
          readOnly={this.props.readOnly}
          onFocus={this.handleOnFocus}
          onBlur={this.handleOnBlur}
          onChange={this.handleOnChange}
          placeholder={this.props.placeholder}
          style={this.props.style}
        />

        <label htmlFor={this.props.name} style={this.props.labelStyles}>
          {this.props.label}
        </label>

        {
          this.props.inputIconComponent ? (
            <div className="input-icon--action" >
              { this.props.inputIconComponent }
            </div>
          ) : this.renderCancelButton()
        }

        <div className="cmp-caption">
        {
          this.props.isInvalid && this.shouldDisplayInvalidMessage() ?
            this.props.invalidHint : this.renderRegularHint()
        }
        </div>

        <div className="cmp-input-messages">
          { this.props.children }
        </div>

      </div>
    );
  }
}
InputWithLabel.propTypes = {
  label: PropTypes.any,
  children: PropTypes.any,
  noForcedHeight: PropTypes.bool,
  noForcedLabel: PropTypes.bool,
  name: PropTypes.string,
  hasFixedLabel: PropTypes.bool,
  alwaysFloating: PropTypes.bool,
  defaultValue: PropTypes.string,
  invalidHint: PropTypes.any,
  regularHint: PropTypes.any,
  value: PropTypes.any.isRequired,
  type: PropTypes.string,
  autoFocus: PropTypes.bool,
  readOnly: PropTypes.bool,
  hideLabel: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  attemptsBeforeInvalidHint: PropTypes.number,
  initialFocus: PropTypes.bool,
  selectAllonFocus: PropTypes.bool,
  autoComplete: PropTypes.oneOf(['on', 'off']),
  autoCapitalize: PropTypes.oneOf(['none', 'sentences', 'words', 'characters']),
  inputIconComponent: PropTypes.object,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  clearValue: PropTypes.func,
  colorTheme: PropTypes.oneOf(['default', 'light']),
  style: PropTypes.object,
  alwayFocus: PropTypes.bool,
  labelStyles: PropTypes.any,
};
InputWithLabel.defaultProps = {
  attemptsBeforeInvalidHint: 0,
};

export default InputWithLabel;
